import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FormControl } from 'react-bootstrap'
import { FiChevronDown, FiChevronUp, FiSearch, FiX, FiCheck } from 'react-icons/fi'

function SearchableMultiSelect ({
  options,
  value = [],
  onChange,
  placeholder = 'Cerca...',
  emptyLabel = 'Nessuna selezione',
  getOptionLabel,
  getOptionValue,
  getOptionSearchText,
  disabled = false
}) {
  const containerRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const selectedValues = useMemo(() => [...new Set(value || [])], [value])
  const selected = useMemo(
    () => options.filter(option => selectedValues.includes(getOptionValue(option))),
    [options, selectedValues, getOptionValue]
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(option => {
      const text = getOptionSearchText ? getOptionSearchText(option) : getOptionLabel(option)
      return (text || '').toLowerCase().includes(q)
    })
  }, [options, query, getOptionLabel, getOptionSearchText])

  useEffect(() => {
    function handleClickOutside (event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function toggleOption (option) {
    if (disabled) return
    const optionValue = getOptionValue(option)
    onChange(selectedValues.includes(optionValue)
      ? selectedValues.filter(item => item !== optionValue)
      : [...selectedValues, optionValue])
  }

  function clearSelection () {
    if (disabled) return
    onChange([])
    setQuery('')
  }

  return (
    <div className={`searchable-select searchable-multi-select${disabled ? ' is-disabled' : ''}`} ref={containerRef}>
      <button
        type='button'
        className='searchable-select-trigger'
        onClick={() => !disabled && setOpen(current => !current)}
        disabled={disabled}
        aria-expanded={open}
      >
        <span className={selected.length ? 'searchable-select-value' : 'searchable-select-placeholder'}>
          {selected.length ? `${selected.length} selezionati` : emptyLabel}
        </span>
        {open ? <FiChevronUp className='searchable-select-chevron' /> : <FiChevronDown className='searchable-select-chevron' />}
      </button>

      {selected.length > 0 && (
        <div className='searchable-multi-select-chips'>
          {selected.map(option => {
            const optionValue = getOptionValue(option)
            return (
              <button
                key={optionValue}
                type='button'
                className='searchable-multi-select-chip'
                disabled={disabled}
                onClick={() => toggleOption(option)}
                title={`Rimuovi ${getOptionLabel(option)}`}
              >
                <span>{getOptionLabel(option)}</span>
                <FiX />
              </button>
            )
          })}
        </div>
      )}

      {open && (
        <div className='searchable-select-panel'>
          <div className='searchable-select-search'>
            <FiSearch className='searchable-select-search-icon' />
            <FormControl
              autoFocus
              value={query}
              placeholder={placeholder}
              disabled={disabled}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
            />
          </div>

          <button
            type='button'
            className='searchable-select-clear'
            onClick={clearSelection}
            disabled={disabled}
          >
            <FiX /> {emptyLabel}
          </button>

          <div className='searchable-select-list'>
            {filtered.length === 0 && (
              <div className='searchable-select-empty'>Nessun risultato</div>
            )}
            {filtered.map(option => {
              const optionValue = getOptionValue(option)
              const isSelected = selectedValues.includes(optionValue)
              return (
                <button
                  key={optionValue}
                  type='button'
                  className={`searchable-select-option${isSelected ? ' is-selected' : ''}`}
                  disabled={disabled}
                  onClick={() => toggleOption(option)}
                >
                  <span>{getOptionLabel(option)}</span>
                  {isSelected && <FiCheck />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchableMultiSelect
